import React, { useRef, useState, useEffect } from 'react';

/**
 * Affichage des erreurs de session
 */
export default function ErrorDisplay({ 
  showErrorMenu, 
  sessionErrors, 
  periods, 
  canvasHeight,
  onPeriodClick,
  onClose,
  onLinkErrorClick 
}) {
  const scrollContainerRef = useRef(null);
  const [showGradient, setShowGradient] = useState(false);

  // Vérifier si le contenu déborde et si on n'est pas en bas du scroll
  useEffect(() => {
    if (!showErrorMenu) return;
    
    const checkScrollState = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const hasOverflow = container.scrollHeight > container.clientHeight;
        
        // Vérifier si on est en bas du scroll (avec une marge de 1px pour les arrondis)
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 1;
        
        // Afficher le dégradé seulement si on peut défiler ET qu'on n'est pas en bas
        setShowGradient(hasOverflow && !isAtBottom);
      }
    };

    // Vérifier au montage et après un court délai pour s'assurer que le contenu est rendu
    checkScrollState();
    const timeoutId = setTimeout(checkScrollState, 100);

    // Ajouter un listener pour détecter le scroll
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollState);
    }

    return () => {
      clearTimeout(timeoutId);
      if (container) {
        container.removeEventListener('scroll', checkScrollState);
      }
    };
  }, [sessionErrors, showErrorMenu]); // Re-vérifier quand les erreurs changent ou quand le menu s'affiche

  if (!showErrorMenu) return null;

  // Mapping des types d'erreurs vers leurs libellés d'affichage
  const errorTypeLabels = {
    'POSITIONS_MANQUANTES': 'Positions manquantes',
    'POSITIONS_EN_TROP': 'Positions en trop',
    'SKILL_MANQUANT': 'Skill manquant',
    'DANSEUR_DUPLIQUE': 'Danseur dupliqué',
    'SKILL_INSUFFISANT': 'Skill insuffisant',
    'SKILL_REQUIS_MANQUANT': 'Skill requis manquant',
    'CHEVAUCHEMENT': 'Chevauchement de périodes',
    'CONFLIT_LIAISONS': 'Conflit de liaisons',
    'COMBINAISON_SKILLS_IMPOSSIBLE': 'Combinaison de skills impossible',
    'POSITIONS_LIEES_INCOMPATIBLES': 'Positions liées incompatibles',
    'CHAINE_DANSEURS_DIFFERENTS': 'Danseurs différents entre positions liées',
    'STUNT_GROUPES_TAILLES_DIFFERENTES': 'Stunt assigné à des groupes de tailles différentes',
    'STUNT_NOMBRE_POSITIONS_INVALIDE': 'Nombre de positions invalide pour le stunt',
    'STUNT_INSUFFISANT': 'Stunt insuffisant',
    'STUNT_REQUIS_MANQUANT': 'Stunt requis manquant',
    'STUNT_SANS_GROUPE': 'Stunt sans groupe',
  };

  // Grouper les erreurs par type et par période
  const groupedErrors = {};
  
  sessionErrors.forEach(error => {
    const errorType = error.type || 'AUTRE';
    const periodName = error.periodName || 'Autre';
    
    if (!groupedErrors[errorType]) {
      groupedErrors[errorType] = {};
    }
    if (!groupedErrors[errorType][periodName]) {
      groupedErrors[errorType][periodName] = [];
    }
    groupedErrors[errorType][periodName].push(error);
  });

  const handleErrorClick = (error) => {
    // Vérifier si c'est une erreur de groupe de liaisons
    if (error.type === 'CONFLIT_LIAISONS') {
      // Extraire les noms des périodes du message
      const periodsMatch = error.message.match(/périodes: ([^)]+)\)/);
      if (periodsMatch && periods && onLinkErrorClick) {
        const periodNamesInError = periodsMatch[1].split(', ').map(name => name.trim());
        // Trouver les indices des périodes
        const periodIndices = periodNamesInError
          .map(name => periods.findIndex(p => p.name === name))
          .filter(idx => idx !== -1);
        
        if (periodIndices.length > 0) {
          onLinkErrorClick(periodIndices);
          if (onClose) onClose();
        }
      }
      return;
    }
    
    // Pour les autres erreurs, utiliser periodName pour naviguer
    if (error.periodName && periods && onPeriodClick) {
      // Pour les chevauchements, prendre la première période
      const periodName = error.periodName.split(', ')[0];
      const periodIndex = periods.findIndex(p => p.name === periodName);
      if (periodIndex !== -1) {
        onPeriodClick(periodIndex);
        if (onClose) onClose();
      }
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#fff',
        border: '2px solid #ffb326ff',
        borderRadius: '8px',
        color: '#e65100',
        fontSize: '13px',
        fontFamily: 'sans-serif',
        fontWeight: '500',
        zIndex: 40,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '300px',
        maxWidth: '400px',
        maxHeight: canvasHeight ? `${canvasHeight - 20}px` : '80vh',
      }}
    >
      {/* Conteneur avec scroll */}
      <div
        ref={scrollContainerRef}
        style={{
          maxHeight: canvasHeight ? `${canvasHeight - 40}px` : 'calc(80vh - 20px)',
          overflowY: 'scroll',
          padding: '10px 16px',
          // Rendre la scrollbar toujours visible
          scrollbarWidth: 'thin',
          scrollbarColor: '#ffb326ff #f5f5f5',
        }}
        className="error-display-scroll"
      >
        {sessionErrors.length === 0 ? (
          <div style={{ fontSize: '13px' }}>Aucune incohérence détectée</div>
        ) : (
          <div>
            {Object.entries(groupedErrors).map(([errorType, periodErrors], typeIndex) => (
              <div key={errorType}>
                {typeIndex > 0 && (
                  <div style={{ 
                    height: '1px', 
                    backgroundColor: '#ffa726', 
                    margin: '10px 0',
                    opacity: 0.3,
                  }} />
                )}
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '6px',
                  color: '#e65100',
                }}>
                  {errorTypeLabels[errorType] || errorType}
                </div>
                {Object.entries(periodErrors).map(([periodName, errors]) => (
                  <div key={`${errorType}-${periodName}`} style={{ marginBottom: '8px' }}>
                    {errors.map((error, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleErrorClick(error)}
                        style={{
                          fontSize: '12px',
                          marginBottom: idx < errors.length - 1 ? '4px' : '0',
                          cursor: 'pointer',
                          padding: '4px 6px',
                          borderRadius: '4px',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff3e0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {error.message}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Dégradé d'opacité en bas pour indiquer plus de contenu - affiché uniquement si scroll nécessaire */}
      {showGradient && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(to bottom, transparent, #fff)',
            pointerEvents: 'none',
            borderRadius: '0 0 6px 6px',
          }}
        />
      )}
      
      <style>{`
        .error-display-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .error-display-scroll::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 4px;
        }
        .error-display-scroll::-webkit-scrollbar-thumb {
          background: #ffb326ff;
          border-radius: 4px;
        }
        .error-display-scroll::-webkit-scrollbar-thumb:hover {
          background: #ffa726;
        }
      `}</style>
    </div>
  );
}
